/**
 * Page permissions configuration
 * Defines all available pages and their default role-based access
 */

export interface PageDefinition {
  key: string;
  name: string;
  href: string;
  group: string;
  defaultRoles: string[];
  requiresFeatureFlag?: string;
  requiresApiKey?: boolean;
}

export const PAGE_GROUPS = {
  operations: 'Operations',
  customers: 'Customers',
  delivery: 'Delivery',
  shop: 'Shop',
  financial: 'Financial',
  communication: 'Communication',
  hr: 'HR & Payroll',
  admin: 'Administration',
} as const;

/**
 * All available pages in the admin application
 * The 'key' is derived from the href (without leading slash)
 */
export const AVAILABLE_PAGES: PageDefinition[] = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    href: '/dashboard',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'customers',
    name: 'Customers',
    href: '/customers',
    group: 'customers',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'],
  },
  {
    key: 'courier-customers',
    name: 'Courier Customers',
    href: '/courier-customers',
    group: 'customers',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isWarehouseApiEnabled',
  },
  {
    key: 'customer-issues',
    name: 'Customer Issues',
    href: '/customer-issues',
    group: 'customers',
    // Front-desk work: any authorised staff rep can log an issue for a walk-in
    // customer. Keep in sync with the route allowedRoles in App.tsx and the
    // SupportTicket ACLs in legacy-api — a role missing from the backend ACL
    // gets a 401, which the admin app treats as a dead session and logs out.
    defaultRoles: [
      'Overlord',
      'Administrator',
      'Super Administrator',
      'Cashier',
      'Store Administrator',
      'Warehouse Administrator',
      'Limited Cashier',
    ],
  },
  {
    key: 'warehouse-billing',
    name: 'Warehouse Billing',
    href: '/warehouse-billing',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isWarehouseApiEnabled',
  },
  {
    key: 'scan-in',
    name: 'Scan In',
    href: '/scan-in',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator'],
    requiresFeatureFlag: 'isScanInEnabled',
  },
  {
    key: 'smart-scan',
    name: 'Smart Scan',
    href: '/smart-scan',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator'],
    requiresFeatureFlag: 'isSmartScanEnabled',
  },
  {
    key: 'packages',
    name: 'Packages',
    href: '/packages',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Warehouse Administrator', 'Store Administrator', 'Limited Cashier'],
  },
  // ── Shop ──
  {
    key: 'shop-for-me',
    name: 'Shop For Me',
    href: '/shop-for-me',
    group: 'shop',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isShopForMeEnabled',
  },
  {
    key: 'shop',
    name: 'Products',
    href: '/shop',
    group: 'shop',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isShopEnabled',
  },
  {
    key: 'shop-categories',
    name: 'Categories',
    href: '/shop-categories',
    group: 'shop',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isShopEnabled',
  },
  {
    key: 'shop-orders',
    name: 'Orders',
    href: '/shop-orders',
    group: 'shop',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isShopEnabled',
  },
  // ── Financial ──
  {
    key: 'invoices',
    name: 'Invoices',
    href: '/invoices',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator'],
  },
  {
    key: 'delivery-request',
    name: 'Package Deliveries',
    href: '/delivery-request',
    group: 'delivery',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isDeliveryEnabled',
  },
  {
    key: 'local-deliveries',
    name: 'Local Deliveries',
    href: '/local-deliveries',
    group: 'delivery',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isLocalDeliveryEnabled',
  },
  {
    key: 'manifests',
    name: 'Manifests',
    href: '/manifests',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
  },
  {
    key: 'point-of-sale',
    name: 'POS - Checkout',
    href: '/point-of-sale',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'],
  },
  {
    key: 'cash-out',
    name: 'Cash Out',
    href: '/cash-out',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'],
  },
  {
    key: 'communication',
    name: 'Communication',
    href: '/communication',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator', 'Store Administrator'],
  },
  {
    key: 'communication-logs',
    name: 'Communication Logs',
    href: '/communication-logs',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator', 'Store Administrator'],
  },
  {
    key: 'marketing',
    name: 'Marketing',
    href: '/marketing',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator', 'Store Administrator'],
  },
  {
    key: 'reviews-gallery',
    name: 'Reviews & Gallery',
    href: '/reviews-gallery',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator', 'Store Administrator'],
  },
  {
    key: 'marketing-agents',
    name: 'Marketing Agents',
    href: '/marketing-agents',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'whatsapp',
    name: 'Whatsapp',
    href: '/whatsapp',
    group: 'communication',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'],
    requiresFeatureFlag: 'whatsappBotEnabled',
  },
  {
    key: 'warehouse',
    name: 'Warehouse Management',
    href: '/warehouse',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresApiKey: true,
  },
  {
    key: 'warehouse-hub',
    name: 'Warehouse Hub',
    href: '/warehouse-hub',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isWarehouseApiEnabled',
  },
  {
    key: 'scan-bonus-report',
    name: 'Scan Bonus Report',
    href: '/scan-bonus-report',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isWarehouseApiEnabled',
  },
  {
    key: 'partner-requirements',
    name: 'Partner Requirements',
    href: '/partner-requirements',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'partner-compliance',
    name: 'Partner Compliance',
    href: '/partner-compliance',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
  },
  {
    key: 'fulfillment-partners',
    name: 'Fulfillment Partners',
    href: '/fulfillment-partners',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isFulfillmentEnabled',
  },
  {
    key: 'fulfillment-orders',
    name: 'Fulfillment Orders',
    href: '/fulfillment-orders',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isFulfillmentEnabled',
  },
  {
    key: 'warehouse-invoices',
    name: 'Warehouse Invoices',
    href: '/warehouse-invoices',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'invoice-requests',
    name: 'Invoice Requests',
    href: '/invoice-requests',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
  },
  {
    key: 'expenses',
    name: 'Expenses',
    href: '/expenses',
    group: 'financial',
    defaultRoles: ['Overlord', 'Administrator'],
    requiresFeatureFlag: 'isExpenseTrackerEnabled',
  },
  {
    key: 'customs-estimator',
    name: 'Customs Estimator',
    href: '/customs-estimator',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier'],
    requiresFeatureFlag: 'isCustomsCalculatorEnabled',
  },
  {
    key: 'users',
    name: 'Users',
    href: '/users',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'rewards',
    name: 'Rewards',
    href: '/rewards',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'settings',
    name: 'Settings',
    href: '/settings',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'attendance',
    name: 'Attendance',
    href: '/attendance',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator', 'Store Administrator'],
    requiresFeatureFlag: 'attendanceEnabled',
  },
  {
    key: 'attendance-report',
    name: 'Attendance Report',
    href: '/attendance-report',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator'],
    requiresFeatureFlag: 'attendanceEnabled',
  },
  {
    key: 'clock-in',
    name: 'Clock In',
    href: '/clock-in',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'],
    requiresFeatureFlag: 'attendanceEnabled',
  },
  {
    key: 'payroll',
    name: 'Payroll',
    href: '/payroll',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator'],
    requiresFeatureFlag: 'payrollEnabled',
  },
  {
    key: 'staff-rates',
    name: 'Staff Rates',
    href: '/staff-rates',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator'],
    requiresFeatureFlag: 'payrollEnabled',
  },
  {
    key: 'payslips',
    name: 'My Payslips',
    href: '/payslips',
    group: 'hr',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'],
    requiresFeatureFlag: 'payrollEnabled',
  },
  {
    key: 'audit-logs',
    name: 'Audit Logs',
    href: '/audit-logs',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'billing',
    name: 'Billing',
    href: '/billing',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator'],
  },
  {
    key: 'help-center',
    name: 'Help Center',
    href: '/help-center',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Limited Cashier'],
  },
  {
    key: 'unknown-packages',
    name: 'Unknown Packages',
    href: '/unknown-packages',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
  },
  {
    key: 'warehouse-interests',
    name: 'Partner Interests',
    href: '/warehouse-interests',
    group: 'operations',
    defaultRoles: ['Overlord', 'Administrator', 'Warehouse Administrator'],
    requiresFeatureFlag: 'isWarehouseApiEnabled',
  },
  {
    key: 'changelog',
    name: "What's New",
    href: '/changelog',
    group: 'admin',
    defaultRoles: ['Overlord', 'Administrator', 'Cashier', 'Store Administrator', 'Warehouse Administrator', 'Limited Cashier'],
  },
  {
    key: 'migrations',
    name: 'Data Migration',
    href: '/migrations',
    group: 'admin',
    defaultRoles: ['Overlord'],
  },
  {
    key: 'dev-board',
    name: 'Dev Board',
    href: '/dev-board',
    group: 'admin',
    defaultRoles: ['Overlord'],
  },
];

/**
 * Get pages grouped by their group name
 */
export function getPagesByGroup(): Record<string, PageDefinition[]> {
  const grouped: Record<string, PageDefinition[]> = {};

  for (const page of AVAILABLE_PAGES) {
    if (!grouped[page.group]) {
      grouped[page.group] = [];
    }
    grouped[page.group].push(page);
  }

  return grouped;
}

/**
 * Get the default permissions for a given role
 */
export function getDefaultPermissionsForRole(roleName: string): Record<string, boolean> {
  const permissions: Record<string, boolean> = {};

  for (const page of AVAILABLE_PAGES) {
    permissions[page.key] = page.defaultRoles.includes(roleName);
  }

  return permissions;
}

/**
 * Get a page definition by its key
 */
export function getPageByKey(key: string): PageDefinition | undefined {
  return AVAILABLE_PAGES.find(p => p.key === key);
}

/**
 * True when the user has custom permissions AND an explicit allow/deny entry
 * for this page. An explicit entry is authoritative: it must be able to ADD a
 * page the role would not see by default, not only remove one, so every
 * downstream role gate (menu, route) must defer to it.
 */
export function hasExplicitPagePermission(
  pagePermissions:
    | { permissions?: Record<string, boolean>; hasCustomPermissions?: boolean }
    | null
    | undefined,
  pageKey: string,
): boolean {
  return !!(
    pagePermissions?.hasCustomPermissions &&
    pagePermissions.permissions?.[pageKey] !== undefined
  );
}

/**
 * Determine whether a user can access a given page.
 *
 * Custom permissions (when present and explicit for the page) take precedence,
 * otherwise we fall back to the primary role's defaults. Pages that are not part
 * of the config are allowed (access is then governed by route `allowedRoles`).
 *
 * This is the single source of truth shared by AdminProtectedRoute (route gating)
 * and AdminShell (subscription-lock redirect target), so the two never disagree
 * about whether a user may reach a page — which previously caused a redirect loop.
 */
export function userCanAccessPage(
  user: { roles?: Array<{ name?: string }> } | null | undefined,
  pagePermissions:
    | { permissions?: Record<string, boolean>; hasCustomPermissions?: boolean }
    | null
    | undefined,
  pageKey: string,
): boolean {
  // Let unauthenticated handling happen elsewhere (e.g. ProtectedRoute).
  if (!user) return true;

  // Custom permissions take first precedence when explicitly set for this page.
  if (hasExplicitPagePermission(pagePermissions, pageKey)) {
    return pagePermissions!.permissions![pageKey];
  }

  // Fall back to role-based access using the primary role's defaults.
  const primaryRole = user.roles?.map(r => r?.name).filter(Boolean)[0];
  if (primaryRole) {
    const roleDefaults = getDefaultPermissionsForRole(primaryRole);
    if (roleDefaults[pageKey] !== undefined) {
      return roleDefaults[pageKey];
    }
  }

  // Page not in config — allow (route-level allowedRoles still applies).
  return true;
}
